/**
 * Service for generating unique export names.
 * Pure functions - no React dependencies.
 */

export type PastExportItem = {
  fileName: string;
};

export type DateFormatter = (isoDate: string) => string;

/**
 * Extracts the numeric suffix from a name like "My Data 2024-01-01 (3)"
 * Returns the number or 0 if no suffix found.
 */
const extractNumericSuffix = (name: string): number => {
  const match = /\((\d+)\)$/.exec(name);
  return match ? Number.parseInt(match[1], 10) : 0;
};

/**
 * Checks if a name exists in the list of past exports (case-insensitive).
 */
const nameExists = (name: string, pastExports: PastExportItem[]): boolean => {
  const lowerName = name.toLowerCase();
  return pastExports.some(({ fileName }) => fileName.toLowerCase() === lowerName);
};

/**
 * Finds the highest numeric suffix used for names starting with the base name.
 */
const findHighestSuffix = (baseName: string, pastExports: PastExportItem[]): number => {
  const lowerBaseName = baseName.toLowerCase();

  const suffixes = pastExports
    .map(({ fileName }) => fileName)
    .filter(name => {
      const lowerName = name.toLowerCase();
      // Must start with base name and have a numeric suffix
      return lowerName.startsWith(lowerBaseName) && /\(\d+\)$/.test(name);
    })
    .map(extractNumericSuffix)
    .filter(num => num > 0);

  return suffixes.length > 0 ? Math.max(...suffixes) : 0;
};

/**
 * Generates a unique export name based on the current date and existing exports.
 *
 * @param pastExports - List of existing exports with fileNames
 * @param formatDate - Function to format a date string
 * @returns A unique export name
 *
 * @example
 * // First export of the day
 * createUniqueExportName([], formatDate) // "My Data Jan 15, 2024"
 *
 * // Second export when first exists
 * createUniqueExportName([{ fileName: "My Data Jan 15, 2024" }], formatDate)
 * // "My Data Jan 15, 2024 (1)"
 */
export const createUniqueExportName = (
  pastExports: PastExportItem[],
  formatDate: DateFormatter
): string => {
  const formattedDate = formatDate(new Date().toISOString());
  const baseName = `My Data ${formattedDate}`;

  // If base name doesn't exist, use it
  if (!nameExists(baseName, pastExports)) {
    return baseName;
  }

  // Find the next available number
  const highestSuffix = findHighestSuffix(baseName, pastExports);
  let counter = highestSuffix + 1;
  let candidateName = `${baseName} (${counter})`;

  // Ensure the name is unique (handles edge cases)
  while (nameExists(candidateName, pastExports)) {
    counter++;
    candidateName = `${baseName} (${counter})`;
  }

  return candidateName;
};

/**
 * Validates an export name.
 * Returns error message if invalid, null if valid.
 */
export const validateExportName = (name: string): string | null => {
  if (!name || name.trim().length === 0) {
    return 'Export name cannot be empty';
  }

  if (name.length > 100) {
    return 'Export name cannot exceed 100 characters';
  }

  // Check for invalid characters
  const invalidChars = /[<>:"/\\|?*]/;
  if (invalidChars.test(name)) {
    return 'Export name contains invalid characters';
  }

  return null;
};

/**
 * Checks if a proposed name conflicts with existing exports.
 */
export const isNameUnique = (name: string, pastExports: PastExportItem[]): boolean => {
  return !nameExists(name, pastExports);
};
